import React, { createContext, useContext, useState, useCallback} from "react";
import {createOrder as createOrderApi, fetchOrder as fetchOrderApi, updateOrder as updateOrderApi,
deleteOrder as deleteOrderApi, fetchSingleOrder} from "./api/api";
import { useAuthenticatedEffect } from "./components/useauthenticatedeffect";

const OrderContext = createContext();

export const useOrders = () => useContext(OrderContext);

export const OrderProvider = ({children}) => {
    const [orders, setOrders] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [error, setError] = useState('');

    const createOrder = async (orderData) => {
        try {
            // call API for backend order creation
            const response = await createOrderApi(orderData);
            setOrders((prevOrders) =>[...prevOrders, response]);
            setError('');
            return response;
        } catch (error) {
            console.error('Error creating order:', error);
            setError('Error creating order. Please try again.');
            throw error;
        }
    };

    const fetchOrders = useCallback(async (page = 1, filters = {}) => {
        try {
            // call API for backend order fetch
            const response = await fetchOrderApi(page, filters);
            // setOrders(response);
            setOrders(response.results);
            setTotalOrders(response.count);
            setError('');
        } catch (error){
            console.error('Error fetching order:', error);
            setError('Error fetching order. Please try again.');
            setOrders([]);
        }
    }, []);

    // fetches all orders pages
    const fetchAllOrders = useCallback(async (filters = {}) => {
        try {
            let page = 1;
            let allResults = [];
            let hasNext = true;

            while (hasNext) {
                const BIG_PAGE_SIZE = 1000; // set a large page size to minimize API calls
                const response = await fetchOrderApi(page, filters, BIG_PAGE_SIZE);
                if (!response || !response.results) break;

                allResults = [...allResults, ...response.results];

                if (response.next) {
                    page += 1;
                } else {
                    hasNext = false;
                }
            }
            // store in state
            setOrders(allResults);
            setTotalOrders(allResults.length);
            return allResults;
        } catch (error) {
            console.error('Error fetching all orders:', error);
            setError('Error fetching all orders. Please try again.');
            return [];
        }
    }, []);

    useAuthenticatedEffect(
        useCallback(() => {
            fetchOrders().then(r => r); // added then(r => r) to remove warning
        }, [fetchOrders])
    );

    const updateOrder = async (orderData) => {
        if (!orderData || !orderData.id) {
            console.error('Invalid order data:', orderData);
            return;
        }
        try {
            // call API for backend order update
            console.log('Sending orderData from orderContext:', orderData);
            const response = await updateOrderApi(orderData);
            setOrders((prevOrders) => prevOrders.map(order => order.id === response.id ? response : order));
            setError('');
        } catch (error){
            console.error('Error updating order:', error);
            setError('Error updating order. Please try again.');
        }
    };

    const deleteOrder = async (orderId) => {
        try {
            // call API for backend order delete
            await deleteOrderApi(orderId);
            setOrders((prevOrders) => prevOrders.filter(order => order.id !== orderId));
            setError('');
        } catch (error) {
            console.error('Error deleting order:', error);
            setError('Error deleting order. Please try again.');
        }
    };

    // after importing fetchSingleOrder from api.js, create helper
    const getOrderById = async (orderId) => {
        try {
            return await fetchSingleOrder(orderId);
        } catch (error) {
            console.error('Error fetching order:', error);
            throw error;
        }
    }

    return (
        <OrderContext.Provider value={{orders, error, totalOrders, createOrder, fetchOrders,
            fetchAllOrders, updateOrder, deleteOrder, getOrderById}}>
            {children}
        </OrderContext.Provider>
    )
}